import MdiHome from '~icons/mdi/home'
import MdiBriefcase from '~icons/mdi/briefcase'
import MdiClipboardCheck from '~icons/mdi/clipboard-check'
import MdiMapMarker from '~icons/mdi/map-marker'
import MdiTagMultiple from '~icons/mdi/tag-multiple'
import MdiMap from '~icons/mdi/map'
import MdiAccountGroup from '~icons/mdi/account-group'
import MdiPackageVariant from '~icons/mdi/package-variant'
import MdiCash from '~icons/mdi/cash'
import MdiTrayArrowUp from '~icons/mdi/tray-arrow-up'
import MdiFolder from '~icons/mdi/folder'
import MdiSwapHorizontal from '~icons/mdi/swap-horizontal'
import MdiWrench from '~icons/mdi/wrench'
import MdiFileDocument from '~icons/mdi/file-document'
import MdiFileCabinet from '~icons/mdi/file-cabinet'
import MdiPlusCircle from '~icons/mdi/plus-circle'
import MdiAccount from '~icons/mdi/account'
import MdiShieldAccount from '~icons/mdi/shield-account'
import MdiPalette from '~icons/mdi/palette'
import MdiRobot from '~icons/mdi/robot'
import MdiCog from '~icons/mdi/cog'
import MdiChartBar from '~icons/mdi/chart-bar'
import MdiTrendingDown from '~icons/mdi/trending-down'
import MdiDatabaseImport from '~icons/mdi/database-import'
import MdiTableEdit from '~icons/mdi/table-edit'
import MdiKeyboardReturn from '~icons/mdi/keyboard-return'

// Shared navigation config — single source of truth for both DesktopSidebar and BottomNav.
//
// bottomNav: 'primary' | 'more' | null
//   'primary' — always-visible slot in the bottom bar (mobile)
//   'more'    — listed in the slide-up "More" panel (mobile)
//   null      — side menu only, never in bottom nav
//
// inSideMenu: whether the item appears in the desktop sidebar
//
// category: controls sidebar group heading and mobile More panel grouping
//   null              — top-level items, shown without a group header on desktop
//   'operations'      — Operations group
//   'management'      — Management group
//   'administration'  — Admin-only group (visible only to tenant_admin / super_admin)
//
// requiredPermission: key from usePermissions() that must be true for the item to be visible.
//   null / omitted — visible to all authenticated users
//
// icon: imported icon component from ~icons/mdi/...
// iconClass: Tailwind text color class applied to the icon

export const NAV_ITEMS = [
  // ── Top-level (no group heading on desktop) ───────────────────────────────
  {
    key: 'home',
    labelKey: 'nav.home',
    to: '/',
    icon: MdiHome,
    iconClass: 'text-green-400',
    activeMatch: (path) => path === '/',
    bottomNav: 'primary',
    inSideMenu: true,
    category: null,
    requiredPermission: null
  },
  {
    key: 'items',
    labelKey: 'nav.items',
    to: '/items',
    icon: MdiBriefcase,
    iconClass: 'text-blue-400',
    activeMatch: (path) => path.startsWith('/item') || path.startsWith('/instance/'),
    bottomNav: 'more',
    inSideMenu: true,
    category: null,
    requiredPermission: 'isBackOffice',
    shellModes: ['office'],
  },
  {
    key: 'import',
    labelKey: 'nav.import',
    to: '/import',
    icon: MdiDatabaseImport,
    iconClass: 'text-violet-400',
    activeMatch: (path) => path.startsWith('/import'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'administration',
    requiredPermission: 'isAdmin'
  },

  // ── Operations ────────────────────────────────────────────────────────────
  {
    key: 'checkout',
    labelKey: 'nav.checkout',
    to: '/checkout/',
    icon: MdiClipboardCheck,
    iconClass: 'text-emerald-400',
    activeMatch: (path) => path === '/checkout/' || path === '/checkout',
    bottomNav: 'primary',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isBackOffice',
    shellModes: ['warehouse', 'office'],
  },
  {
    key: 'return',
    labelKey: 'nav.return',
    to: '/return',
    icon: MdiKeyboardReturn,
    iconClass: 'text-amber-300',
    activeMatch: (path) => path === '/return' || path.startsWith('/return/'),
    bottomNav: 'primary',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isBackOffice',
    shellModes: ['warehouse', 'office'],
  },
  {
    key: 'categories',
    labelKey: 'nav.categories',
    to: '/categories',
    icon: MdiTagMultiple,
    iconClass: 'text-violet-400',
    activeMatch: (path) => path.startsWith('/categories'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isManagerOrAbove',
    shellModes: ['office'],
  },
  {
    key: 'checkouts-overview',
    labelKey: 'nav.checkoutsOverview',
    to: '/checkouts',
    icon: MdiTrayArrowUp,
    iconClass: 'text-cyan-400',
    activeMatch: (path) => path.startsWith('/checkouts'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isBackOffice',
    shellModes: ['warehouse', 'office'],
  },
  {
    key: 'projects',
    labelKey: 'nav.projects',
    to: '/projects',
    icon: MdiFolder,
    iconClass: 'text-indigo-400',
    activeMatch: (path) => path.startsWith('/projects') || path.startsWith('/project/'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isBackOffice',
    shellModes: ['warehouse', 'office'],
  },
  {
    key: 'bundles',
    labelKey: 'nav.bundles',
    to: '/bundles',
    icon: MdiPackageVariant,
    iconClass: 'text-amber-400',
    activeMatch: (path) => path.startsWith('/bundles'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isOperatorOrAbove',
    shellModes: ['office'],
  },
  {
    key: 'transfers',
    labelKey: 'nav.transfers',
    to: '/transfers',
    icon: MdiSwapHorizontal,
    iconClass: 'text-purple-400',
    activeMatch: (path) => path.startsWith('/transfers'),
    bottomNav: 'primary',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isOperatorOrAbove',
    shellModes: ['warehouse', 'office'],
  },
  {
    key: 'maintenance',
    labelKey: 'nav.maintenance',
    to: '/maintenance/schedules',
    icon: MdiWrench,
    iconClass: 'text-orange-300',
    activeMatch: (path) => path.startsWith('/maintenance'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isOperatorOrAbove'
  },
  {
    key: 'automations',
    labelKey: 'nav.automations',
    to: '/automations',
    icon: MdiRobot,
    iconClass: 'text-emerald-400',
    activeMatch: (path) => path.startsWith('/automations'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isOperatorOrAbove'
  },

  // ── Management ────────────────────────────────────────────────────────────
  {
    key: 'bulk-edit',
    labelKey: 'bulkEdit.title',
    to: '/bulk-edit',
    icon: MdiTableEdit,
    iconClass: 'text-emerald-300',
    activeMatch: (path) => path.startsWith('/bulk-edit'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'management',
    requiredPermission: 'isBackOffice'
  },
  {
    key: 'locations',
    labelKey: 'nav.locations',
    to: '/locations',
    icon: MdiMapMarker,
    iconClass: 'text-orange-400',
    activeMatch: (path) => path.startsWith('/location'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'management',
    requiredPermission: 'isBackOffice',
    shellModes: ['office'],
  },
  {
    key: 'areas',
    labelKey: 'nav.areas',
    to: '/areas',
    icon: MdiMap,
    iconClass: 'text-teal-400',
    activeMatch: (path) => path.startsWith('/areas'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'management',
    requiredPermission: 'isBackOffice'
  },
  {
    key: 'customers',
    labelKey: 'nav.customers',
    to: '/customers',
    icon: MdiAccountGroup,
    iconClass: 'text-sky-400',
    activeMatch: (path) => path.startsWith('/customers'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'management',
    requiredPermission: 'isManagerOrAbove'
  },
  {
    key: 'profile',
    labelKey: 'nav.profile',
    to: '/profile',
    icon: MdiAccount,
    iconClass: 'text-rose-400',
    activeMatch: (path) => path.startsWith('/profile'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'management',
    requiredPermission: null
  },
  {
    key: 'price-groups',
    labelKey: 'nav.priceGroups',
    to: '/price-groups',
    icon: MdiCash,
    iconClass: 'text-yellow-400',
    activeMatch: (path) => path.startsWith('/price-groups'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'management',
    requiredPermission: 'isManagerOrAbove'
  },
  {
    key: 'documents',
    labelKey: 'nav.documents',
    to: '/documents',
    icon: MdiFileCabinet,
    iconClass: 'text-teal-300',
    activeMatch: (path) => path.startsWith('/documents'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'operations',
    requiredPermission: 'isBackOffice'
  },
  {
    key: 'wiki',
    labelKey: 'nav.wiki',
    to: '/wiki',
    icon: MdiFileDocument,
    iconClass: 'text-slate-300',
    activeMatch: (path) => path.startsWith('/wiki'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'management',
    requiredPermission: 'isBackOffice'
  },

  // ── Administration (tenant_admin / super_admin only) ──────────────────────
  {
    key: 'portal-management',
    labelKey: 'portal.management',
    to: '/portal-admin',
    icon: MdiAccountGroup,
    iconClass: 'text-green-400',
    activeMatch: path => path.startsWith('/portal-admin'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'administration',
    requiredPermission: 'isOperatorOrAbove',
  },
  {
    key: 'admin-users',
    labelKey: 'nav.adminUsers',
    to: '/admin/users',
    icon: MdiShieldAccount,
    iconClass: 'text-red-400',
    activeMatch: (path) => path.startsWith('/admin/users'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'administration',
    requiredPermission: 'isAdmin'
  },
  {
    key: 'admin-branding',
    labelKey: 'nav.adminBranding',
    to: '/admin/branding',
    icon: MdiPalette,
    iconClass: 'text-pink-400',
    activeMatch: (path) => path.startsWith('/admin/branding'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'administration',
    requiredPermission: 'isAdmin'
  },
  {
    key: 'admin-macros',
    labelKey: 'nav.adminMacros',
    to: '/admin/macros',
    icon: MdiCog,
    iconClass: 'text-emerald-300',
    activeMatch: (path) => path.startsWith('/admin/macros'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'administration',
    requiredPermission: 'isAdmin'
  },
  {
    key: 'asset-values',
    labelKey: 'nav.assetValues',
    to: '/admin/asset-values',
    icon: MdiChartBar,
    iconClass: 'text-lime-400',
    activeMatch: (path) => path.startsWith('/admin/asset-values'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'administration',
    requiredPermission: 'isAdmin'
  },
  {
    key: 'depreciation-profiles',
    labelKey: 'nav.depreciationProfiles',
    to: '/admin/depreciation-profiles',
    icon: MdiTrendingDown,
    iconClass: 'text-amber-400',
    activeMatch: (path) => path.startsWith('/admin/depreciation-profiles'),
    bottomNav: 'more',
    inSideMenu: true,
    category: 'administration',
    requiredPermission: 'isAdmin'
  }
]

export const bottomPrimaryItems = NAV_ITEMS.filter((i) => i.bottomNav === 'primary')
export const bottomMoreItems = NAV_ITEMS.filter((i) => i.bottomNav === 'more')
export const sideMenuItems = NAV_ITEMS.filter((i) => i.inSideMenu)
